import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, ThemeProvider, createTheme } from '@mui/material/styles';
import { lightTheme, darkTheme } from '../resources/style/theme';
import { sendThemeModeToNativeApp } from '../utils/webview';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
    mode: ThemeMode;
    toggleTheme: () => void;
    setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderWrapperProps {
    children: ReactNode;
}

export const ThemeProviderWrapper: React.FC<ThemeProviderWrapperProps> = ({ children }) => {
    const [mode, setMode] = useState<ThemeMode>(() => {
        // Check localStorage for saved theme preference
        const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
        return savedMode || 'light';
    });

    useEffect(() => {
        // Save theme preference to localStorage
        localStorage.setItem('theme-mode', mode);
        // Let the browser render its own chrome (scrollbars, native form controls) in the same mode
        document.documentElement.style.colorScheme = mode;
        // Hand the mode over to the native wrappers so the status bar and the navigation bar follow
        sendThemeModeToNativeApp(mode);
    }, [mode]);

    const toggleTheme = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const setTheme = (newMode: ThemeMode) => {
        setMode(newMode);
    };

    const theme = mode === 'dark' ? darkTheme : lightTheme;
    const appTheme: Theme = createTheme(theme);

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme, setTheme }}>
            <ThemeProvider theme={appTheme}>{children}</ThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProviderWrapper');
    }
    return context;
};
